import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, Stethoscope } from 'lucide-react';
import SEO from '@/components/SEO';

export const MedicalDisclaimerPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Medical Disclaimer"
        description="Important medical disclaimer for BMI Calc Hub. BMI is a screening tool, not a diagnostic tool. Always consult healthcare professionals."
        canonical="/disclaimer"
      />
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Calculator
          </Button>
        </Link>

        {/* Important Warning Banner */}
        <div className="bg-warning-light border border-warning/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-warning flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">Important Notice</h2>
              <p className="text-muted-foreground">
                BMI Calc Hub is an informational tool only. The information provided should NOT be used 
                as a substitute for professional medical advice, diagnosis, or treatment. Always consult 
                with a qualified healthcare provider for health-related decisions.
              </p>
            </div>
          </div>
        </div>

        <Card className="card-elevated">
          <CardContent className="p-8 sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Medical Disclaimer</h1>
                <p className="text-sm text-muted-foreground">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. General Disclaimer</h2>
                <p>
                  The information provided by BMI Calc Hub ("we," "us," or "our") on bmicalchub.com is for 
                  general informational and educational purposes only. All information on the site is provided 
                  in good faith; however, we make no representation or warranty of any kind, express or implied, 
                  regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any 
                  information on the site.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. Not Medical Advice</h2>
                <p>
                  <strong>THE CONTENT ON THIS WEBSITE IS NOT INTENDED TO BE A SUBSTITUTE FOR PROFESSIONAL 
                  MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT.</strong>
                </p>
                <p className="mt-4">
                  Always seek the advice of your physician or other qualified health provider with any questions 
                  you may have regarding a medical condition. Never disregard professional medical advice or delay 
                  in seeking it because of something you have read on this website.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. BMI Limitations</h2>
                <p>
                  Body Mass Index (BMI) is a screening tool, not a diagnostic tool. BMI has significant limitations:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Does not distinguish between fat and muscle:</strong> Athletes and muscular individuals 
                    may have a high BMI without excess body fat
                  </li>
                  <li>
                    <strong>Does not account for fat distribution:</strong> Where fat is located matters for health risks
                  </li>
                  <li>
                    <strong>Not suitable for everyone:</strong> May be less accurate for elderly individuals, 
                    pregnant women, and certain ethnic groups
                  </li>
                  <li>
                    <strong>Age and gender limitations:</strong> Standard BMI categories may not apply equally 
                    to all populations
                  </li>
                  <li>
                    <strong>Does not measure overall health:</strong> Many factors beyond weight affect health
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Athlete Mode Disclaimer</h2>
                <p>
                  Our "Athlete Mode" feature provides alternative interpretations of BMI for physically active 
                  individuals. This feature:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Is based on general guidelines and research findings</li>
                  <li>Is NOT a substitute for professional sports medicine advice</li>
                  <li>May not be appropriate for all athletes or activity levels</li>
                  <li>Should be used as a general guide only</li>
                  <li>Does not account for individual body composition differences</li>
                </ul>
                <p className="mt-4">
                  Athletes should work with qualified sports medicine professionals, nutritionists, and coaches 
                  for personalized guidance.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">5. Additional Metrics Disclaimer</h2>
                <p>
                  Other metrics provided by our calculator (Waist-to-Height Ratio, BMI Prime, Ponderal Index, 
                  Hydration Estimates, Running Pace Impact) are:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>General estimates based on population-level research</li>
                  <li>Not personalized medical recommendations</li>
                  <li>Subject to individual variation</li>
                  <li>Not validated for all populations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">6. Emergency Situations</h2>
                <p className="font-semibold text-foreground">
                  IF YOU THINK YOU MAY HAVE A MEDICAL EMERGENCY, CALL YOUR DOCTOR OR EMERGENCY SERVICES IMMEDIATELY.
                </p>
                <p className="mt-4">
                  Do not rely on information from this website for emergency medical needs. This website does not 
                  recommend or endorse any specific tests, physicians, products, procedures, opinions, or other 
                  information that may be mentioned.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">7. No Doctor-Patient Relationship</h2>
                <p>
                  Use of this website does not create a doctor-patient relationship. The information provided:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Is general in nature</li>
                  <li>Is not tailored to your specific health situation</li>
                  <li>Cannot account for all individual factors</li>
                  <li>Should not be relied upon for medical decisions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">8. Accuracy of Information</h2>
                <p>
                  While we strive to provide accurate and up-to-date information, we cannot guarantee that all 
                  information is complete, accurate, or current. Health guidelines and recommendations may change 
                  over time as new research becomes available.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">9. User Responsibility</h2>
                <p>
                  By using this website, you acknowledge and agree that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You are responsible for your own health decisions</li>
                  <li>You will consult qualified healthcare providers for medical advice</li>
                  <li>You will not hold BMI Calc Hub liable for any health outcomes</li>
                  <li>The information is used at your own risk</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">10. When to Seek Medical Help</h2>
                <p>
                  We recommend consulting a healthcare professional if:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You have concerns about your weight or BMI</li>
                  <li>You're planning significant dietary or exercise changes</li>
                  <li>You have existing health conditions</li>
                  <li>You're taking medications that may affect weight</li>
                  <li>You're experiencing unexplained weight changes</li>
                  <li>You're pregnant or planning to become pregnant</li>
                  <li>You have an eating disorder or disordered eating patterns</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">11. External Links</h2>
                <p>
                  This website may contain links to external websites. We do not endorse or guarantee the 
                  accuracy of information on linked sites. External links are provided for convenience only.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">12. Contact Information</h2>
                <p>
                  If you have questions about this Medical Disclaimer, please contact us at:
                </p>
                <p className="mt-2">
                  <strong>Email:</strong> info@bmicalchub.com<br />
                  <strong>Website:</strong> bmicalchub.com
                </p>
              </section>

              {/* Final Notice */}
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-6 mt-8">
                <p className="text-sm text-center font-medium text-foreground">
                  By using BMI Calc Hub, you acknowledge that you have read, understood, and agree to this 
                  Medical Disclaimer. If you do not agree, please do not use our services.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default MedicalDisclaimerPage;
